import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CipherService } from './cipher.service';
import {
  EstadoTramiteSolicitud,
  parseListadoEstadoTramiteResponse
} from '../utils/estado-tramite.util';
import {
  ConsultarFacturaRequest,
  FacturaItem,
  parseListadoFacturasResponse
} from '../utils/factura.util';
import {
  CertificadoRevocacion,
  MotivoRevocacion,
  PreguntasRetoResponse,
  RevocacionSolicitante,
  parseCertificadosResponse,
  parseMotivosResponse
} from '../utils/revocacion.util';

/**
 * Servicios de Gestión (postventa transversal).
 * Cifrado `{ data }` alineado a Canal (estadoTramite / certificado / validación).
 * Descarga factura: gateway AWS + token Cognito (sin cifrado de body).
 */
@Injectable({ providedIn: 'root' })
export class GestionService {
  private readonly estadoTramiteBase = environment.estadoTramiteServiceUrl;
  private readonly descargaFacturaUrl = environment.descargaFacturaUrl;
  private readonly certificadoBase = environment.certificadoServiceUrl;
  private readonly validacionBase = environment.validacionIdentidadServiceUrl;

  private cognitoCache: { token: string; expiresAt: number } | null = null;
  /** Cache en memoria de la sesión (sin sessionStorage). */
  private motivosCache: MotivoRevocacion[] | null = null;
  private motivosInflight$: Observable<MotivoRevocacion[]> | null = null;

  constructor(
    private http: HttpClient,
    private cipher: CipherService
  ) {}

  consultarSolicitudesEstadoTramite(request: {
    tipo: string;
    identificacion: string;
  }): Observable<EstadoTramiteSolicitud[]> {
    return this.postEncrypted<unknown>(
      `${this.estadoTramiteBase}/estado-solicitud/listar`,
      request
    ).pipe(map((res) => parseListadoEstadoTramiteResponse(res)));
  }

  consultarSolicitanteRevocacion(request: {
    tipo: string;
    numero: string;
  }): Observable<RevocacionSolicitante> {
    return this.postEncrypted<RevocacionSolicitante>(
      `${this.certificadoBase}/revocacion/solicitante`,
      request
    );
  }

  listarCertificadosRevocacion(numeroIdentificacion: string): Observable<CertificadoRevocacion[]> {
    return this.postEncrypted<unknown>(
      `${this.certificadoBase}/revocacion/listar`,
      numeroIdentificacion
    ).pipe(map((res) => parseCertificadosResponse(res)));
  }

  /**
   * Motivos de revocación: se piden una vez por sesión de app (cache en memoria).
   * Al recargar la página se vuelven a consultar.
   */
  consultarMotivosRevocacion(): Observable<MotivoRevocacion[]> {
    if (this.motivosCache?.length) {
      return of(this.motivosCache);
    }
    if (this.motivosInflight$) {
      return this.motivosInflight$;
    }

    this.motivosInflight$ = this.getEncrypted<unknown>(
      `${this.certificadoBase}/revocacion/motivos`
    ).pipe(
      map((res) => parseMotivosResponse(res)),
      tap((motivos) => {
        if (motivos.length) {
          this.motivosCache = motivos;
        }
        this.motivosInflight$ = null;
      }),
      catchError((err) => {
        this.motivosInflight$ = null;
        return throwError(() => err);
      }),
      shareReplay(1)
    );
    return this.motivosInflight$;
  }

  revocarCertificado(body: CertificadoRevocacion): Observable<{ mensaje?: string }> {
    return this.postEncrypted<{ mensaje?: string }>(
      `${this.certificadoBase}/revocacion/revocar`,
      body
    );
  }

  consultarPreguntasReto(request: {
    solicitudTitularId: number;
    tipoDocumento: string;
    numeroDocumento: string;
    nombres: string;
    apellidos: string;
    codigoDepartamento: string;
    codigoMunicipio: string;
    fechaExpedicionDocumento: string;
    ip: string;
  }): Observable<PreguntasRetoResponse> {
    return this.postEncrypted<PreguntasRetoResponse>(
      `${this.validacionBase}/validacion-identidad/preguntas`,
      request
    );
  }

  validarRespuestasReto(body: {
    preguntas: unknown[];
    ip: string;
    solicitudTitularId: number;
    applicationId: string;
  }): Observable<unknown> {
    return this.postEncrypted<unknown>(
      `${this.validacionBase}/validacion-identidad/respuestas`,
      body
    );
  }

  consultarFacturas(request: ConsultarFacturaRequest): Observable<FacturaItem[]> {
    return this.getCognitoAccessToken().pipe(
      switchMap((token) =>
        this.http.post<unknown>(this.descargaFacturaUrl, request, {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: token
          })
        })
      ),
      map((res) => parseListadoFacturasResponse(res)),
      catchError((err) => {
        if (err?.status === 401 || err?.status === 403) {
          this.cognitoCache = null;
        }
        return throwError(() => err);
      })
    );
  }

  private getCognitoAccessToken(): Observable<string> {
    const now = Date.now();
    if (this.cognitoCache && this.cognitoCache.expiresAt > now) {
      return of(this.cognitoCache.token);
    }

    const body = new HttpParams()
      .set('grant_type', 'client_credentials')
      .set('client_id', environment.cognitoClientId)
      .set('client_secret', environment.cognitoClientSecret)
      .set('scope', environment.cognitoScope);

    return this.http
      .post<{ access_token?: string; expires_in?: number }>(
        environment.cognitoTokenUrl,
        body.toString(),
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          })
        }
      )
      .pipe(
        map((res) => {
          const token = String(res?.access_token ?? '').trim();
          if (!token) {
            throw new Error('No fue posible autenticar la consulta de factura.');
          }
          const expiresInSec = Math.max(60, Number(res?.expires_in ?? 3600) - 60);
          this.cognitoCache = {
            token,
            expiresAt: Date.now() + expiresInSec * 1000
          };
          return token;
        })
      );
  }

  private postEncrypted<T>(url: string, body: unknown): Observable<T> {
    const headers = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    if (!this.cipher.isEncryptionEnabled) {
      return this.http.post<T>(url, body, headers);
    }

    return from(this.cipher.encrypt(JSON.stringify(body))).pipe(
      switchMap((encrypted) =>
        this.http.post<{ data?: string } | T>(url, { data: encrypted }, headers)
      ),
      switchMap((response) => this.unwrapCipherResponse<T>(response))
    );
  }

  private getEncrypted<T>(url: string): Observable<T> {
    const headers = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.get<{ data?: string } | T>(url, headers).pipe(
      switchMap((response) => this.unwrapCipherResponse<T>(response))
    );
  }

  private unwrapCipherResponse<T>(response: { data?: string } | T): Observable<T> {
    const wrapped = response as { data?: unknown };
    if (wrapped?.data && typeof wrapped.data === 'string') {
      return from(this.cipher.decrypt(wrapped.data)).pipe(
        map((plain) => JSON.parse(plain) as T)
      );
    }
    return of(response as T);
  }
}
